<template>
  <Teleport to="body">
    <Transition name="dk-mnav">
      <div v-if="open" class="dk-mnav-root" role="dialog" aria-modal="true" aria-label="Menu">
        <div class="dk-mnav__backdrop" @click="open = false" />
        <aside class="dk-mnav__panel">
          <header class="dk-mnav__head">
            <strong>{{ bootstrap.storeName || "Menu" }}</strong>
            <button class="dk-mnav__x" aria-label="Close menu" @click="open = false">✕</button>
          </header>

          <nav class="dk-mnav__body">
            <MobileNavItem v-for="(item, i) in items" :key="i" :item="item" @navigate="open = false" />
          </nav>

          <footer class="dk-mnav__foot">
            <RouterLink to="/search" class="dk-mnav__link" @click="open = false">Search</RouterLink>
            <RouterLink
              :to="account.isAuthed ? '/account' : '/account/login'"
              class="dk-mnav__link"
              @click="open = false"
            >{{ account.isAuthed ? "Account" : "Sign in" }}</RouterLink>
          </footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, watch } from "vue"
import { useRoute } from "vue-router"
import { useAccountStore } from "../stores/account"
import { useBootstrapStore, type MenuItem } from "../stores/bootstrap"
import MobileNavItem from "./MobileNavItem.vue"

defineProps<{ items: MenuItem[] }>()
const open = defineModel<boolean>("open", { default: false })

const bootstrap = useBootstrapStore()
const account = useAccountStore()
const route = useRoute()

// Close on navigation (mirrors CartDrawer).
watch(() => route.fullPath, () => { open.value = false })

// Close on Escape + lock body scroll while open.
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") open.value = false
}
watch(open, (v) => {
  document.body.style.overflow = v ? "hidden" : ""
  if (v) window.addEventListener("keydown", onKey)
  else window.removeEventListener("keydown", onKey)
})
onBeforeUnmount(() => {
  document.body.style.overflow = ""
  window.removeEventListener("keydown", onKey)
})
</script>

<style scoped>
.dk-mnav-root {
  position: fixed;
  inset: 0;
  z-index: 60;
}
.dk-mnav__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}
.dk-mnav__panel {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: min(360px, 88vw);
  display: flex;
  flex-direction: column;
  background: var(--dk-color-bg, #fff);
  color: var(--dk-color-fg, #111);
  box-shadow: 12px 0 40px rgba(0, 0, 0, 0.18);
}
.dk-mnav__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid var(--dk-color-border, #e5e7eb);
  font-size: var(--dk-fs-base);
}
.dk-mnav__x {
  background: none;
  border: 0;
  font-size: var(--dk-fs-lg);
  cursor: pointer;
  color: inherit;
  line-height: 1;
}
.dk-mnav__body {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}
.dk-mnav__foot {
  border-top: 1px solid var(--dk-color-border, #e5e7eb);
  padding: 0.5rem 0 calc(0.5rem + env(safe-area-inset-bottom));
}
/* Links + accordion rows (used by MobileNavItem too — unscoped selectors below). */
</style>

<style>
.dk-mnav__link {
  display: block;
  padding: 0.85rem 1.25rem;
  color: var(--dk-color-fg, #111);
  text-decoration: none;
  font-size: var(--dk-fs-base);
}
.dk-mnav__link:hover {
  background: var(--dk-surface);
}
.dk-mnav__link--all {
  font-size: var(--dk-fs-sm);
  opacity: 0.7;
}
.dk-mnav__group > .dk-mnav__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.25rem;
  cursor: pointer;
  font-size: var(--dk-fs-base);
  list-style: none;
}
.dk-mnav__group > .dk-mnav__summary::-webkit-details-marker {
  display: none;
}
.dk-mnav__caret {
  font-size: var(--dk-fs-xs);
  opacity: 0.6;
  transition: transform 0.15s;
}
.dk-mnav__group[open] > .dk-mnav__summary .dk-mnav__caret {
  transform: rotate(180deg);
}
/* Indent each nested level. */
.dk-mnav__children {
  padding-left: 0.75rem;
  border-left: 2px solid var(--dk-color-border, #eee);
  margin-left: 1.25rem;
}
.dk-mnav__children .dk-mnav__link,
.dk-mnav__children .dk-mnav__summary {
  padding-left: 0.5rem;
}
</style>

<style scoped>
/* slide-from-left + fade transition */
.dk-mnav-enter-active,
.dk-mnav-leave-active {
  transition: opacity 0.25s ease;
}
.dk-mnav-enter-active .dk-mnav__panel,
.dk-mnav-leave-active .dk-mnav__panel {
  transition: transform 0.28s cubic-bezier(0.16, 0.84, 0.44, 1);
}
.dk-mnav-enter-from,
.dk-mnav-leave-to {
  opacity: 0;
}
.dk-mnav-enter-from .dk-mnav__panel,
.dk-mnav-leave-to .dk-mnav__panel {
  transform: translateX(-100%);
}
@media (prefers-reduced-motion: reduce) {
  .dk-mnav-enter-active,
  .dk-mnav-leave-active,
  .dk-mnav-enter-active .dk-mnav__panel,
  .dk-mnav-leave-active .dk-mnav__panel {
    transition: none;
  }
}
</style>
